import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Send, ChevronUp, ChevronDown, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SelectionSubmitForm({
  galleryId,
  publicId,
  selectedPhotoIds = [],
  onSubmitSuccess,
  onClearSelection,
}) {
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.client_name.trim()) {
      toast.error('Imie i nazwisko jest wymagane');
      return;
    }

    if (!form.client_email.trim()) {
      toast.error('Email jest wymagany');
      return;
    }

    if (selectedPhotoIds.length === 0) {
      toast.error('Wybierz przynajmniej jedno zdjecie');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/public/gallery/${publicId}/select`, {
        selected_photo_ids: selectedPhotoIds,
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        notes: form.notes,
      });

      setSubmitted(true);
      toast.success('Twoj wybor zostal wyslany!');

      setTimeout(() => {
        setExpanded(false);
        setSubmitted(false);
        setForm({ client_name: '', client_email: '', client_phone: '', notes: '' });
        if (onSubmitSuccess) onSubmitSuccess();
        if (onClearSelection) onClearSelection();
      }, 2000);
    } catch (error) {
      console.error('Selection submit error:', error);
      toast.error('Nie udalo sie wyslac wyboru. Sprobuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  if (selectedPhotoIds.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-2xl z-50"
    >
      <div className="max-w-4xl mx-auto">
        {/* Collapsed Bar */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-primary">
              <Star className="w-5 h-5 fill-current" />
              <span className="font-semibold">{selectedPhotoIds.length}</span>
            </div>
            <span className="text-sm text-muted-foreground">
              zdjec wybranych
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
            >
              Wyczysc
            </Button>
            <Button
              onClick={() => setExpanded(!expanded)}
              className="gap-2"
            >
              <Send className="w-4 h-4" />
              Wyslij wybor
              {expanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Expanded Form */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 border-t border-border pt-4">
                {submitted ? (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 mx-auto mb-4 bg-green-500/10 rounded-full flex items-center justify-center">
                      <Check className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Wybor wyslany!</h3>
                    <p className="text-sm text-muted-foreground">
                      Fotograf otrzyma powiadomienie o Twoim wyborze.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Input
                        placeholder="Imie i nazwisko *"
                        value={form.client_name}
                        onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Input
                        type="email"
                        placeholder="Email *"
                        value={form.client_email}
                        onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Input
                        type="tel"
                        placeholder="Telefon (opcjonalnie)"
                        value={form.client_phone}
                        onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Textarea
                        placeholder="Dodatkowe uwagi (opcjonalnie)"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={1}
                        className="resize-none"
                      />
                    </div>
                    <div className="sm:col-span-2 flex justify-end">
                      <Button type="submit" disabled={submitting} className="gap-2">
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Wyslij wybor ({selectedPhotoIds.length} zdjec)
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
